import React, { useRef, useState, useEffect } from "react";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaPlay,
  FaPause,
  FaVolumeUp,
} from "react-icons/fa";

type QuestionType =
  | "MULTIPLE_CHOICE"
  | "MULTIPLE_CHOICE_4"
  | "TRUE_FALSE"
  | "TEXT_INPUT"
  | "ESSAY"
  | "EMAIL"
  | "LETTER"
  | "AUDIO_MULTIPLE_CHOICE"
  | "AUDIO_TRUE_FALSE"
  | "MATCHING"
  | "DRAG_DROP"
  | "FILL_BLANKS"
  | "SENTENCE_ORDER";

type Question = {
  id: number;
  questionNumber: number;
  questionType: QuestionType;
  questionText: string;
  contextText?: string;
  options?: string[] | string;
  correctAnswer?: string;
  points: number;
  audioUrl?: string | null;
  audioDownloadUrl?: string | null;
  imageUrl?: string | null;
  imageDownloadUrl?: string | null;
  effectiveAudioDownloadUrl?: string | null;
  module?: string;
  usesSharedContext?: boolean;
  hasQuestionAudio?: boolean;
  hasQuestionImage?: boolean;
};

type QuestionDisplayProps = {
  question: Question;
  answer: string;
  onAnswerChange: (answer: string) => void;
  disabled?: boolean;
  showCorrectAnswer?: boolean;
  correctAnswer?: string;
};

const QuestionDisplay: React.FC<QuestionDisplayProps> = ({
  question,
  answer,
  onAnswerChange,
  disabled = false,
  showCorrectAnswer = false,
  correctAnswer,
}) => {
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioHasPlayed, setAudioHasPlayed] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const audioUrl =
    question.audioDownloadUrl ||
    question.effectiveAudioDownloadUrl ||
    question.audioUrl;
  const imageUrl = question.imageDownloadUrl || question.imageUrl;

  const getOptionsArray = (): string[] => {
    if (!question.options) return [];
    if (Array.isArray(question.options)) return question.options;
    if (typeof question.options === "string") {
      if (question.options.trim().startsWith("[")) {
        try {
          const parsed = JSON.parse(question.options);
          if (Array.isArray(parsed))
            return parsed.filter((opt) => opt && opt.trim());
        } catch (e) {
          console.error("Failed to parse options:", e);
        }
      }
      return question.options
        .split("|")
        .map((opt) => opt.trim())
        .filter((opt) => opt.length > 0);
    }
    return [];
  };

  const handleAudioToggle = () => {
    if (!audioRef.current || audioHasPlayed) return;
    if (isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    } else {
      audioRef.current.play();
      setIsAudioPlaying(true);
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      const audio = audioRef.current;
      audio.onloadedmetadata = () => setAudioDuration(audio.duration);
      audio.ontimeupdate = () => setAudioCurrentTime(audio.currentTime);
      audio.onended = () => {
        setIsAudioPlaying(false);
        setAudioHasPlayed(true);
      };
      audio.onpause = () => setIsAudioPlaying(false);
      audio.onplay = () => setIsAudioPlaying(true);
    }
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getQuestionTypeIcon = () => {
    const icons: Record<QuestionType, string> = {
      MULTIPLE_CHOICE: "○",
      MULTIPLE_CHOICE_4: "○",
      TRUE_FALSE: "✓",
      TEXT_INPUT: "✍️",
      ESSAY: "📝",
      EMAIL: "✉️",
      LETTER: "📄",
      AUDIO_MULTIPLE_CHOICE: "🎧",
      AUDIO_TRUE_FALSE: "🎧",
      MATCHING: "↔️",
      DRAG_DROP: "⇄",
      FILL_BLANKS: "___",
      SENTENCE_ORDER: "123",
    };
    return icons[question.questionType];
  };

  const getQuestionTypeLabel = () => {
    const labels: Record<QuestionType, string> = {
      MULTIPLE_CHOICE: "Multiple Choice",
      MULTIPLE_CHOICE_4: "Multiple Choice (4)",
      TRUE_FALSE: "Richtig/Falsch",
      TEXT_INPUT: "Text Input",
      ESSAY: "Essay",
      EMAIL: "E-Mail",
      LETTER: "Brief",
      AUDIO_MULTIPLE_CHOICE: "Audio MCQ",
      AUDIO_TRUE_FALSE: "Audio R/F",
      MATCHING: "Zuordnung",
      DRAG_DROP: "Drag & Drop",
      FILL_BLANKS: "Lückentext",
      SENTENCE_ORDER: "Satzordnung",
    };
    return labels[question.questionType];
  };

  const renderQuestionInput = () => {
    switch (question.questionType) {
      case "MULTIPLE_CHOICE":
      case "MULTIPLE_CHOICE_4":
      case "AUDIO_MULTIPLE_CHOICE":
        const options = getOptionsArray();
        return (
          <div className="space-y-2">
            {options.map((option, index) => {
              const isSelected = answer === option;
              const isCorrect = showCorrectAnswer && option === correctAnswer;
              const isIncorrect =
                showCorrectAnswer && isSelected && option !== correctAnswer;

              return (
                <label
                  key={`${question.id}-${option}`}
                  className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    disabled ? "cursor-not-allowed opacity-70" : ""
                  } ${
                    isCorrect
                      ? "border-green-500 bg-green-50"
                      : isIncorrect
                      ? "border-red-500 bg-red-50"
                      : isSelected
                      ? "border-[#009fe3] bg-blue-50"
                      : "border-gray-200 hover:border-gray-400 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={option}
                    checked={isSelected}
                    onChange={(e) => onAnswerChange(e.target.value)}
                    disabled={disabled}
                    className="w-4 h-4 sm:w-5 sm:h-5 text-[#009fe3] focus:ring-[#009fe3]"
                  />
                  <span className="inline-flex items-center justify-center min-w-[24px] sm:min-w-[28px] h-6 sm:h-7 bg-gray-200 text-gray-700 font-bold rounded text-xs sm:text-sm px-1.5">
                    {String.fromCharCode(97 + index)}
                  </span>
                  <span className="text-sm sm:text-base text-gray-900 flex-1">
                    {option}
                  </span>
                  {isCorrect && (
                    <FaCheckCircle className="text-green-600 text-lg sm:text-xl" />
                  )}
                  {isIncorrect && (
                    <FaTimesCircle className="text-red-600 text-lg sm:text-xl" />
                  )}
                </label>
              );
            })}
          </div>
        );

      case "MATCHING":
        // ✅ UPDATED: Simplified for individual MATCHING questions
        // Each question now has a single letter answer (e.g., "a", "b", "c")
        // Student selects one letter from dropdown

        const availableLetters = [
          "a",
          "b",
          "c",
          "d",
          "e",
          "f",
          "g",
          "h",
          "i",
          "j",
          "k",
          "l",
        ];

        const selectedLetter = answer ? answer.toLowerCase().trim() : "";
        const isCorrect =
          showCorrectAnswer &&
          selectedLetter === correctAnswer?.toLowerCase().trim();
        const isIncorrect =
          showCorrectAnswer &&
          selectedLetter &&
          selectedLetter !== correctAnswer?.toLowerCase().trim();

        return (
          <div className="space-y-3">
            {/* Single Dropdown Selector */}
            <div
              className={`flex items-center gap-3 p-3 sm:p-4 border-2 rounded-lg transition-all ${
                disabled ? "opacity-70" : ""
              } ${
                isCorrect
                  ? "border-green-500 bg-green-50"
                  : isIncorrect
                  ? "border-red-500 bg-red-50"
                  : "border-gray-300 bg-white"
              }`}
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#009fe3] to-[#0088cc] text-white rounded-lg flex items-center justify-center font-bold text-lg sm:text-xl shrink-0">
                {question.questionNumber}
              </div>
              <span className="text-lg sm:text-xl text-gray-600 shrink-0">
                →
              </span>
              <select
                value={selectedLetter}
                onChange={(e) => onAnswerChange(e.target.value)}
                disabled={disabled}
                className={`flex-1 px-3 py-2 sm:px-4 sm:py-3 border-2 rounded-lg font-semibold focus:ring-2 focus:ring-[#009fe3] focus:outline-none text-sm sm:text-base ${
                  isCorrect
                    ? "border-green-500 text-green-700 bg-green-50"
                    : isIncorrect
                    ? "border-red-500 text-red-700 bg-red-50"
                    : "border-[#009fe3] text-[#009fe3] bg-white"
                } ${disabled ? "cursor-not-allowed" : ""}`}
              >
                <option value="">--- Wählen Sie einen Buchstaben ---</option>
                {availableLetters.map((letter) => (
                  <option key={letter} value={letter}>
                    {letter.toUpperCase()}
                  </option>
                ))}
              </select>
              {isCorrect && (
                <FaCheckCircle className="text-green-600 text-xl sm:text-2xl shrink-0" />
              )}
              {isIncorrect && (
                <FaTimesCircle className="text-red-600 text-xl sm:text-2xl shrink-0" />
              )}
            </div>

            {/* Selected Answer Display */}
            {selectedLetter && (
              <div className="p-3 bg-blue-50 border-2 border-[#009fe3] rounded-lg">
                <div className="text-xs sm:text-sm font-bold text-[#009fe3] mb-2">
                  📝 Ihre Auswahl:
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 sm:px-4 sm:py-2 bg-white border-2 border-[#009fe3] rounded-md text-sm sm:text-base font-bold text-[#009fe3]">
                    {question.questionNumber} → {selectedLetter.toUpperCase()}
                  </span>
                </div>
              </div>
            )}

            {/* Show Correct Answer */}
            {showCorrectAnswer && correctAnswer && (
              <div className="p-3 bg-green-50 border-2 border-green-500 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FaCheckCircle className="text-green-600 text-lg sm:text-xl" />
                  <span className="text-xs sm:text-sm font-bold text-green-800">
                    Richtige Antwort:
                  </span>
                </div>
                <span className="inline-block px-3 py-1 sm:px-4 sm:py-2 bg-white border-2 border-green-500 rounded-md text-sm sm:text-base font-bold text-green-700">
                  {question.questionNumber} → {correctAnswer.toUpperCase()}
                </span>
              </div>
            )}
          </div>
        );

      case "TRUE_FALSE":
      case "AUDIO_TRUE_FALSE":
        return (
          <div className="space-y-2">
            {["Richtig", "Falsch"].map((option) => {
              const isSelected = answer === option;
              const isCorrect = showCorrectAnswer && option === correctAnswer;
              const isIncorrect =
                showCorrectAnswer && isSelected && option !== correctAnswer;

              return (
                <label
                  key={option}
                  className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    disabled ? "cursor-not-allowed opacity-70" : ""
                  } ${
                    isCorrect
                      ? "border-green-500 bg-green-50"
                      : isIncorrect
                      ? "border-red-500 bg-red-50"
                      : isSelected
                      ? "border-[#009fe3] bg-blue-50"
                      : "border-gray-300 hover:border-[#009fe3] hover:bg-blue-50"
                  }`}
                >
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={option}
                    checked={isSelected}
                    onChange={(e) => onAnswerChange(e.target.value)}
                    disabled={disabled}
                    className="w-4 h-4 sm:w-5 sm:h-5 text-[#009fe3] focus:ring-[#009fe3]"
                  />
                  <span className="flex-1 text-sm sm:text-base text-gray-900 font-semibold">
                    {option === "Richtig" ? "✓ Richtig" : "✗ Falsch"}
                  </span>
                  {isCorrect && (
                    <FaCheckCircle className="text-green-600 text-lg sm:text-xl" />
                  )}
                  {isIncorrect && (
                    <FaTimesCircle className="text-red-600 text-lg sm:text-xl" />
                  )}
                </label>
              );
            })}
          </div>
        );

      case "TEXT_INPUT":
      case "FILL_BLANKS":
      case "DRAG_DROP":
      case "SENTENCE_ORDER":
        return (
          <div>
            <input
              type="text"
              value={answer}
              onChange={(e) => onAnswerChange(e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2 sm:px-4 sm:py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-[#009fe3] disabled:bg-gray-100 text-sm sm:text-base"
              placeholder={
                question.questionType === "SENTENCE_ORDER"
                  ? "z.B.: 3, 1, 4, 2"
                  : "Ihre Antwort..."
              }
            />
            {showCorrectAnswer && correctAnswer && (
              <div className="mt-2 p-2 sm:p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs sm:text-sm font-semibold text-green-800">
                  Richtige Antwort: {correctAnswer}
                </p>
              </div>
            )}
          </div>
        );

      case "ESSAY":
      case "EMAIL":
      case "LETTER":
        return (
          <div>
            <textarea
              value={answer}
              onChange={(e) => onAnswerChange(e.target.value)}
              disabled={disabled}
              rows={question.questionType === "ESSAY" ? 10 : 8}
              className="w-full px-3 py-2 sm:px-4 sm:py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-[#009fe3] disabled:bg-gray-100 text-sm sm:text-base"
              placeholder={
                question.questionType === "EMAIL"
                  ? "E-Mail schreiben..."
                  : question.questionType === "LETTER"
                  ? "Brief schreiben..."
                  : "Aufsatz schreiben..."
              }
            />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0 mt-2 text-xs sm:text-sm text-gray-600">
              <span>
                {question.questionType === "ESSAY" && "150-200 Wörter"}
                {question.questionType === "EMAIL" && "80-100 Wörter"}
                {question.questionType === "LETTER" && "100-120 Wörter"}
              </span>
              <span className="font-semibold">Zeichen: {answer.length}</span>
            </div>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={answer}
            onChange={(e) => onAnswerChange(e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 sm:px-4 sm:py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] text-sm sm:text-base"
          />
        );
    }
  };

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-md border border-gray-200 overflow-hidden">
      {/* Compact Header */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#009fe3] to-[#0088cc] text-white rounded-lg flex items-center justify-center font-bold text-base sm:text-lg shrink-0">
              {question.questionNumber}
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              <span className="text-lg sm:text-xl shrink-0">
                {getQuestionTypeIcon()}
              </span>
              <span className="text-xs sm:text-sm text-gray-600 font-medium truncate">
                {getQuestionTypeLabel()}
              </span>
            </div>
          </div>
          <div className="inline-flex items-center px-2 sm:px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-bold text-xs sm:text-sm shrink-0">
            {question.points}P
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Audio Player - Compact */}
        {(question.questionType === "AUDIO_MULTIPLE_CHOICE" ||
          question.questionType === "AUDIO_TRUE_FALSE") &&
          audioUrl && (
            <div className="p-3 sm:p-4 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-300 rounded-lg">
              <div className="flex items-center gap-2 sm:gap-3 mb-2">
                <button
                  onClick={handleAudioToggle}
                  disabled={disabled || audioHasPlayed}
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${
                    audioHasPlayed
                      ? "bg-gray-400 cursor-not-allowed"
                      : isAudioPlaying
                      ? "bg-gradient-to-r from-red-500 to-pink-500"
                      : "bg-gradient-to-r from-indigo-600 to-purple-600"
                  }`}
                >
                  {isAudioPlaying ? (
                    <FaPause className="text-white text-sm sm:text-base" />
                  ) : (
                    <FaPlay className="text-white text-sm sm:text-base ml-0.5" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 text-xs sm:text-sm font-bold text-indigo-900 mb-1">
                    <FaVolumeUp />
                    Audio
                  </div>
                  <p className="text-xs text-indigo-700 truncate">
                    {audioHasPlayed ? "✓ Abgespielt" : "⚠️ Nur 1x abspielbar"}
                  </p>
                </div>
              </div>

              {audioDuration > 0 && (
                <div className="space-y-1">
                  <div className="w-full bg-white/50 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all"
                      style={{
                        width: `${(audioCurrentTime / audioDuration) * 100}%`,
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-indigo-700">
                    <span>{formatTime(audioCurrentTime)}</span>
                    <span>{formatTime(audioDuration)}</span>
                  </div>
                </div>
              )}

              <audio
                ref={audioRef}
                src={audioUrl}
                preload="metadata"
                className="hidden"
              />
            </div>
          )}

        {/* Question-Specific Context - Always Visible */}
        {!question.usesSharedContext && question.contextText && (
          <div className="p-3 sm:p-4 bg-amber-50 border border-amber-300 rounded-lg">
            <div className="flex items-start gap-2">
              <div className="text-amber-600 text-lg sm:text-xl">📄</div>
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-amber-900 leading-relaxed whitespace-pre-wrap">
                  {question.contextText}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Question Image - Always Visible */}
        {imageUrl && (
          <div className="p-2 sm:p-3 bg-gray-50 border border-gray-300 rounded-lg">
            <img
              src={imageUrl}
              alt={`Frage ${question.questionNumber}`}
              className="w-full h-auto rounded-lg shadow-sm"
              onError={(e) => {
                console.error("Failed to load image:", imageUrl);
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        )}

        {/* Question Text */}
        <div className="p-3 bg-blue-50 border-l-4 border-[#009fe3] rounded">
          <p className="text-sm sm:text-base font-semibold text-gray-900 leading-relaxed">
            {question.questionText}
          </p>
        </div>

        {/* Answer Input */}
        {renderQuestionInput()}

        {/* Compact Instructions */}
        {question.questionType === "SENTENCE_ORDER" && (
          <div className="p-2 bg-yellow-50 border border-yellow-300 rounded text-xs text-yellow-900">
            💡 Reihenfolge: "3, 1, 4, 2"
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionDisplay;
